<template>
  <div class="space-y-6">
    <!-- Gewogen Pipeline Overzicht -->
    <div
      class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden"
    >
      <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-2">
          <SparklesIcon class="h-5 w-5 text-purple-500" />
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">Gewogen Pipeline (ML)</h3>
          <button
            @click="showPipelineInfo = !showPipelineInfo"
            class="text-gray-400 hover:text-purple-500 dark:hover:text-purple-400 transition-colors"
            title="Uitleg over de gewogen pipeline"
          >
            <InformationCircleIcon class="h-5 w-5" />
          </button>
        </div>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Omzet gewogen op ML-voorspelde winkans per fase
        </p>
      </div>

      <InfoBox v-if="showPipelineInfo" title="Hoe werkt de gewogen pipeline?" color="purple">
        <p>
          Deze tabel toont alle open deals gegroepeerd per salesfase (Verkenning, Lead, Prospect).
          Per fase wordt de totale omzet vermenigvuldigd met de
          <strong>ML-voorspelde winkans</strong> om de <strong>gewogen omzet</strong> te berekenen.
        </p>
        <p>
          Het ML-model berekent per deal een individuele winkans op basis van kenmerken zoals
          dealgrootte, doorlooptijd, branche en fase. Dit geeft een realistischer beeld dan een vast
          percentage per fase.
        </p>
        <p>
          <strong>Winkans (ML)</strong> — het gemiddelde van de ML-voorspellingen per fase.
          <strong>Winkans (handmatig)</strong> — het vaste percentage op basis van historische
          conversieratio's per fase.
        </p>
      </InfoBox>

      <div v-if="isScoring || isLoadingPipeline" class="px-5 py-8 text-center">
        <div
          class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-purple-600 mx-auto"
        ></div>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {{ isScoring ? 'ML voorspellingen berekenen...' : 'Pipeline laden...' }}
        </p>
      </div>

      <div
        v-else-if="pipelineError"
        class="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        {{ pipelineError }}
      </div>

      <div v-else-if="pipeline" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th
                class="px-5 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Fase
              </th>
              <th
                class="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Deals
              </th>
              <th
                class="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Omzet
              </th>
              <th
                class="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Gewogen omzet
              </th>
              <th
                class="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Winkans (ML)
              </th>
              <th
                class="px-5 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Winkans (handmatig)
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="stage in pipeline.stages" :key="stage.stage">
              <td class="px-5 py-3 text-sm font-medium text-gray-900 dark:text-white">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 rounded-full" :class="stageColor(stage.stage)"></div>
                  {{ stage.stage }}
                </div>
              </td>
              <td class="px-5 py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                {{ stage.deal_count }}
              </td>
              <td class="px-5 py-3 text-sm text-right text-gray-700 dark:text-gray-300">
                {{ formatCurrency(stage.total_revenue) }}
              </td>
              <td
                class="px-5 py-3 text-sm text-right font-medium text-green-600 dark:text-green-400"
              >
                {{ formatCurrency(stage.weighted_revenue) }}
              </td>
              <td class="px-5 py-3 text-sm text-right">
                <span
                  v-if="stage.avg_ml_probability != null"
                  class="font-medium"
                  :class="probabilityColor(stage.avg_ml_probability)"
                >
                  {{ Math.round(stage.avg_ml_probability * 100) }}%
                </span>
                <span v-else class="text-gray-400">-</span>
              </td>
              <td class="px-5 py-3 text-sm text-right text-gray-500 dark:text-gray-400">
                {{ Math.round(stage.avg_manual_probability * 100) }}%
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <td class="px-5 py-3 text-sm font-semibold text-gray-900 dark:text-white">Totaal</td>
              <td class="px-5 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                {{ pipeline.totals.total_deals }}
              </td>
              <td class="px-5 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
                {{ formatCurrency(pipeline.totals.total_revenue) }}
              </td>
              <td
                class="px-5 py-3 text-sm text-right font-semibold text-green-600 dark:text-green-400"
              >
                {{ formatCurrency(pipeline.totals.total_weighted_revenue) }}
              </td>
              <td class="px-5 py-3"></td>
              <td class="px-5 py-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Funnel Prognose -->
    <div
      class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden"
    >
      <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2">
              <ChartBarIcon class="h-5 w-5 text-blue-500" />
              <h3 class="text-lg font-medium text-gray-900 dark:text-white">Funnel Prognose</h3>
              <button
                @click="showFunnelInfo = !showFunnelInfo"
                class="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                title="Uitleg over de prognose"
              >
                <InformationCircleIcon class="h-5 w-5" />
              </button>
            </div>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Verwachte ontwikkeling van de salesfunnel
            </p>
          </div>
          <div class="flex gap-2">
            <button
              v-for="w in weekOptions"
              :key="w"
              @click="selectWeeks(w)"
              class="px-3 py-1 text-sm rounded-md transition-colors"
              :class="
                selectedWeeks === w
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              "
            >
              {{ w }}w
            </button>
          </div>
        </div>
      </div>

      <InfoBox v-if="showFunnelInfo" title="Hoe werkt deze prognose?" color="blue">
        <p>
          De prognose simuleert hoe deals door de salesfunnel bewegen met een
          <strong>Markov-ketensimulatie</strong>. Transitierates (hoe snel deals van fase wisselen,
          gewonnen of verloren worden) komen uit historische saleskansen en worden genormaliseerd
          zodat ze per fase optellen tot maximaal 100%. Per week verplaatst een percentage van de
          deals zich naar de volgende fase, Won of Lost.
        </p>
        <p>De grafiek toont drie lijnen:</p>
        <p>
          <strong>Huidige funnel</strong> (blauw, dalend) — de waarde van bestaande deals als er
          geen nieuwe deals binnenkomen. Deals stromen door de pipeline en worden gewonnen of
          verloren, waardoor de totale omzet in de funnel geleidelijk daalt.
        </p>
        <p>
          <strong>Nieuwe instroom</strong> (groen, stijgend) — de verwachte bijdrage van nieuwe
          deals die wekelijks de Verkenningsfase binnenkomen. De instroomsnelheid is de
          <strong>mediaan</strong> van het aantal deals per kalenderweek uit historische data.
        </p>
        <p>
          <strong>Totaal</strong> (paars, gestippeld) — de som van beide: de gecombineerde verwachte
          pipeline-omzet per week. Dit is de meest realistische prognose.
        </p>
        <p>
          De winkansen worden bijgesteld op basis van de
          <strong>ML-voorspellingen</strong> van de huidige deals: als het ML-model een hogere
          winkans inschat dan het historisch gemiddelde, wordt de Won-rate proportioneel
          opgeschaald. De tabel onder de grafiek toont de weekdetails: deals per fase, verwachte
          gewonnen omzet en cumulatief totaal.
        </p>
      </InfoBox>

      <!-- Line Chart -->
      <div v-if="forecast?.chart_data && forecast.has_transition_data" class="px-5 pt-4">
        <VChart :option="chartOption" autoresize :style="{ width: '100%', height: '300px' }" />
      </div>

      <div v-if="isLoadingForecast" class="px-5 py-8 text-center">
        <div
          class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600 mx-auto"
        ></div>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Prognose berekenen...</p>
      </div>

      <div
        v-else-if="forecastError"
        class="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        {{ forecastError }}
      </div>

      <div v-else-if="forecast" class="p-5 space-y-4">
        <!-- Summary -->
        <div
          v-if="forecast.total_expected_won_revenue"
          class="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg"
        >
          <div class="flex-shrink-0">
            <div
              class="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center"
            >
              <CurrencyEuroIcon class="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div>
            <div class="text-sm font-medium text-green-800 dark:text-green-300">
              Verwachte gewonnen omzet komende {{ selectedWeeks }} weken
            </div>
            <div class="text-lg font-bold text-green-700 dark:text-green-400">
              {{ formatCurrency(forecast.total_expected_won_revenue) }}
            </div>
          </div>
        </div>

        <div
          v-if="!forecast.has_transition_data"
          class="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm text-yellow-800 dark:text-yellow-300"
        >
          Deze functie is nog in ontwikkeling.
        </div>

        <!-- Forecast table -->
        <div v-if="forecast.has_transition_data" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead>
              <tr>
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
                >
                  Week
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
                >
                  Verkenning
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
                >
                  Lead
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
                >
                  Prospect
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
                >
                  Verwacht gewonnen
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"
                >
                  Cumulatief
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700/50">
              <tr
                v-for="week in forecast.weeks"
                :key="week.week"
                class="hover:bg-gray-50 dark:hover:bg-gray-700/30"
              >
                <td class="px-3 py-2 text-gray-900 dark:text-white font-medium">{{ week.week }}</td>
                <td class="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                  {{ week.stages['Verkenningsfase']?.deals?.toFixed(0) || 0 }}
                </td>
                <td class="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                  {{ week.stages['Lead fase']?.deals?.toFixed(0) || 0 }}
                </td>
                <td class="px-3 py-2 text-right text-gray-600 dark:text-gray-400">
                  {{ week.stages['Prospect fase']?.deals?.toFixed(0) || 0 }}
                </td>
                <td class="px-3 py-2 text-right text-green-600 dark:text-green-400">
                  {{ formatCurrency(week.expected_won_revenue || 0) }}
                </td>
                <td class="px-3 py-2 text-right font-medium text-green-700 dark:text-green-300">
                  {{ formatCurrency(week.cumulative_won_revenue || 0) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
  SparklesIcon,
  ChartBarIcon,
  CurrencyEuroIcon,
  InformationCircleIcon
} from '@heroicons/vue/24/outline'
import InfoBox from './InfoBox.vue'
import { predictionService } from '@/services/predictionService.js'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const pipeline = ref(null)
const forecast = ref(null)
const isLoadingPipeline = ref(false)
const isLoadingForecast = ref(false)
const isScoring = ref(false)
const pipelineError = ref('')
const forecastError = ref('')
const selectedWeeks = ref(12)
const showPipelineInfo = ref(false)
const showFunnelInfo = ref(false)
const weekOptions = [4, 8, 12]

// Dark mode detection
const isDarkMode = ref(false)
const updateDarkMode = () => {
  isDarkMode.value = document.documentElement.classList.contains('dark')
}

let observer = null
onMounted(() => {
  updateDarkMode()
  observer = new MutationObserver(updateDarkMode)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})
onUnmounted(() => {
  observer?.disconnect()
})

const euroFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0
})

function formatCurrency(amount) {
  if (!amount) return '\u20AC 0'
  return `\u20AC ${new Intl.NumberFormat('nl-NL').format(Math.round(amount))}`
}

function stageColor(stage) {
  switch (stage) {
    case 'Lead fase':
      return 'bg-blue-500'
    case 'Verkenningsfase':
      return 'bg-yellow-500'
    case 'Prospect fase':
      return 'bg-green-500'
    default:
      return 'bg-gray-500'
  }
}

function probabilityColor(prob) {
  if (prob >= 0.7) return 'text-green-600 dark:text-green-400'
  if (prob >= 0.4) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

function selectWeeks(w) {
  selectedWeeks.value = w
  loadForecast()
}

const chartOption = computed(() => {
  const cd = forecast.value?.chart_data
  if (!cd) return {}

  const dark = isDarkMode.value
  const textColor = dark ? '#e5e7eb' : '#374151'
  const textSecondary = dark ? '#9ca3af' : '#6b7280'
  const borderColor = dark ? '#4b5563' : '#e5e7eb'
  const tooltipBg = dark ? '#1f2937' : '#ffffff'
  const tooltipBorder = dark ? '#374151' : '#e5e7eb'

  return {
    animation: true,
    grid: {
      left: 70,
      right: 20,
      top: 40,
      bottom: 30
    },
    legend: {
      top: 0,
      textStyle: { color: textColor, fontSize: 12 },
      data: ['Huidige funnel', 'Nieuwe instroom', 'Totaal']
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: tooltipBg,
      borderColor: tooltipBorder,
      textStyle: { color: textColor, fontSize: 12 },
      formatter: params => {
        if (!params?.length) return ''
        const week = params[0].axisValue
        let html = `<div style="margin-bottom:4px;font-weight:600;color:${textColor};">Week ${week}</div>`
        for (const p of params) {
          html += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0;">`
          html += `<span style="display:inline-block;width:10px;height:10px;background:${p.color};border-radius:2px;"></span>`
          html += `<span>${p.seriesName}:</span>`
          html += `<span style="margin-left:auto;font-weight:500;">${euroFormatter.format(p.value)}</span>`
          html += `</div>`
        }
        return html
      }
    },
    xAxis: {
      type: 'category',
      data: cd.labels.map(l => `${l}`),
      axisLabel: { color: textSecondary, fontSize: 11 },
      axisLine: { lineStyle: { color: borderColor } },
      axisTick: { alignWithLabel: true, lineStyle: { color: borderColor } },
      name: 'Week',
      nameTextStyle: { color: textSecondary, fontSize: 11 }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: textSecondary,
        fontSize: 11,
        formatter: v => {
          if (v >= 1000000) return `\u20AC${(v / 1000000).toFixed(1)}M`
          if (v >= 1000) return `\u20AC${(v / 1000).toFixed(0)}k`
          return `\u20AC${v}`
        }
      },
      axisLine: { lineStyle: { color: borderColor } },
      splitLine: { lineStyle: { color: borderColor, type: 'dashed' } }
    },
    series: [
      {
        name: 'Huidige funnel',
        type: 'line',
        data: cd.current_pipeline,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: '#3b82f6', width: 2 },
        itemStyle: { color: '#3b82f6' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: dark ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.15)' },
              { offset: 1, color: 'rgba(59,130,246,0)' }
            ]
          }
        }
      },
      {
        name: 'Nieuwe instroom',
        type: 'line',
        data: cd.new_inflow,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { color: '#10b981', width: 2 },
        itemStyle: { color: '#10b981' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: dark ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.15)' },
              { offset: 1, color: 'rgba(16,185,129,0)' }
            ]
          }
        }
      },
      {
        name: 'Totaal',
        type: 'line',
        data: cd.total,
        smooth: true,
        symbol: 'diamond',
        symbolSize: 6,
        lineStyle: { color: '#8b5cf6', width: 2, type: 'dashed' },
        itemStyle: { color: '#8b5cf6' }
      }
    ]
  }
})

async function loadPipeline() {
  isLoadingPipeline.value = true
  pipelineError.value = ''
  try {
    pipeline.value = await predictionService.getPipeline()
  } catch {
    pipelineError.value = 'Kon pipeline data niet laden'
  } finally {
    isLoadingPipeline.value = false
  }
}

async function loadForecast() {
  isLoadingForecast.value = true
  forecastError.value = ''
  try {
    forecast.value = await predictionService.getForecast(selectedWeeks.value)
  } catch {
    forecastError.value = 'Kon prognose niet laden'
  } finally {
    isLoadingForecast.value = false
  }
}

onMounted(async () => {
  isScoring.value = true
  try {
    await predictionService.scoreAll()
  } catch {
    // Non-blocking: if scoring fails, still load pipeline with fallback values
  } finally {
    isScoring.value = false
  }
  loadPipeline()
  loadForecast()
})
</script>
